import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { Loader2, UserRound } from 'lucide-react';
import { useState, type KeyboardEvent, type MouseEvent } from 'react';
import type { UserRef } from '../../types';
import { userName } from '../calculation-request-badges';

interface InlineAssigneeCellProps {
    value: UserRef | null | undefined;
    /** Null unassigns. Absent onUpdate means read-only. */
    onUpdate?: (userId: string | null) => Promise<void>;
    engineers: UserRef[];
    className?: string;
    readOnly?: boolean;
}

/**
 * Asana-style assignee field: the engineer is data, not a workflow state.
 *
 * The pick list is limited to users holding the Engineer role (supplied by the
 * controller); the server validates the same constraint independently.
 */
export function InlineAssigneeCell({ value, onUpdate, engineers, className, readOnly = false }: InlineAssigneeCellProps) {
    const { t } = useTranslations();
    const isMobile = useIsMobile();
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleSelect = async (userId: string | null) => {
        if (userId === (value?.id ?? null) || !onUpdate) {
            setIsOpen(false);
            return;
        }

        setIsLoading(true);
        try {
            await onUpdate(userId);
            setIsOpen(false);
        } catch {
            // Surfaced as a toast by the caller.
        } finally {
            setIsLoading(false);
        }
    };

    const openSelector = (event?: MouseEvent | KeyboardEvent) => {
        event?.stopPropagation();
        if (!isLoading) {
            setIsOpen(true);
        }
    };

    const display = value ? (
        <span className="truncate text-sm">{userName(value)}</span>
    ) : (
        <span className="text-muted-foreground flex items-center gap-1.5 text-sm">
            <UserRound className="h-3.5 w-3.5" />
            {t('calculation_requests.tables.unassigned', 'Unassigned')}
        </span>
    );

    if (readOnly || !onUpdate) {
        return <div className={cn('flex w-full items-center justify-start px-2 py-1.5', className)}>{display}</div>;
    }

    const trigger = (
        <div
            role="button"
            tabIndex={isLoading ? -1 : 0}
            onClick={openSelector}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openSelector(event);
                }
            }}
            className={cn('hover:bg-accent flex w-full items-center justify-start rounded px-2 py-1.5 transition-colors', className)}
            aria-disabled={isLoading}
        >
            {isLoading ? <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" /> : display}
        </div>
    );

    const items = (
        <>
            {engineers.map((engineer) => (
                <DropdownMenuItem key={engineer.id} onClick={() => handleSelect(engineer.id)} className="cursor-pointer">
                    {userName(engineer)}
                </DropdownMenuItem>
            ))}
            {value ? (
                <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleSelect(null)} className="text-muted-foreground cursor-pointer">
                        {t('calculation_requests.tables.unassigned', 'Unassigned')}
                    </DropdownMenuItem>
                </>
            ) : null}
        </>
    );

    if (isMobile) {
        return (
            <ResponsiveModal open={isOpen} onOpenChange={setIsOpen} variant="dialog">
                <ResponsiveModalTrigger asChild>{trigger}</ResponsiveModalTrigger>
                <ResponsiveModalContent dialogClassName="sm:max-w-md" onClick={(event) => event.stopPropagation()}>
                    <ResponsiveModalHeader>
                        <ResponsiveModalTitle>{t('calculation_requests.tables.assignee', 'Engineer')}</ResponsiveModalTitle>
                    </ResponsiveModalHeader>
                    <ResponsiveModalBody className="flex flex-col gap-2">
                        {engineers.map((engineer) => (
                            <button
                                key={engineer.id}
                                type="button"
                                onClick={() => handleSelect(engineer.id)}
                                className="bg-background hover:bg-accent flex w-full items-center justify-start rounded-md border px-3 py-2 text-left text-sm"
                            >
                                {userName(engineer)}
                            </button>
                        ))}
                        {value ? (
                            <button
                                type="button"
                                onClick={() => handleSelect(null)}
                                className="bg-background hover:bg-accent text-muted-foreground flex w-full items-center justify-start rounded-md border px-3 py-2 text-left text-sm"
                            >
                                {t('calculation_requests.tables.unassigned', 'Unassigned')}
                            </button>
                        ) : null}
                    </ResponsiveModalBody>
                </ResponsiveModalContent>
            </ResponsiveModal>
        );
    }

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
            <DropdownMenuContent align="start" onClick={(event) => event.stopPropagation()}>
                {items}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
